import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.mfa_required) {
        setMfaToken(res.mfa_token);
      } else {
        navigate("/app");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyMfa(mfaToken, code);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-[#0A1B2E] text-white font-body flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-white/10 relative overflow-hidden">
        <div className="grain-overlay" />
        <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight relative z-10">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
          <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
        </Link>
        <div className="relative z-10">
          <h1 className="font-display text-5xl font-light leading-tight">
            Every dollar accounted for. Every vendor accountable.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md">
            Sign in to your firm's marketing control center.
          </p>
        </div>
        <p className="font-mono-data text-xs text-zinc-600 relative z-10">© 2026 RetainProof™</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        {mfaToken ? (
          <form onSubmit={submitMfa} className="w-full max-w-sm" data-testid="mfa-form">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/5 border border-white/10 mb-5">
              <ShieldCheck className="h-6 w-6 text-[#D5A44A]" />
            </div>
            <h2 className="font-display text-4xl font-light">Two-factor</h2>
            <p className="text-zinc-500 text-sm mt-2 mb-8">
              Enter the 6-digit code from your authenticator app (or a backup code).
            </p>
            {error && (
              <div className="mb-4 border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3" data-testid="mfa-error">
                {error}
              </div>
            )}
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              placeholder="123456"
              data-testid="mfa-code-input"
              className="bg-white/5 border-white/15 rounded-none text-white h-12 text-center tracking-[0.4em] text-lg"
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="mfa-submit-button"
              className="w-full mt-6 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-11 font-medium"
            >
              {loading ? "Verifying…" : "Verify & sign in"}
            </Button>
            <button
              type="button"
              onClick={() => { setMfaToken(""); setCode(""); setError(""); }}
              className="mt-6 text-sm text-zinc-500 hover:text-white"
              data-testid="mfa-back-button"
            >
              ← Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
            <h2 className="font-display text-4xl font-light">Sign in</h2>
            <p className="text-zinc-500 text-sm mt-2 mb-8">Welcome back to your dashboard.</p>

            {error && (
              <div className="mb-4 border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-widest font-mono-data">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="mt-2 bg-white/5 border-white/15 rounded-none text-white h-11"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-widest font-mono-data">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="mt-2 bg-white/5 border-white/15 rounded-none text-white h-11"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full mt-8 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-11 font-medium group"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </Button>

            <p className="mt-6 text-sm text-zinc-500">
              No account?{" "}
              <Link to="/register" className="text-[#D5A44A] hover:underline" data-testid="to-register-link">
                Create your firm
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
